import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserPlus, Pencil, UserX, Building2, Mail, Phone, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function HRUsersTab({ clientId }) {
  const [users, setUsers] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    assigned_entities: []
  });

  useEffect(() => {
    if (clientId) {
      fetchUsers();
      fetchEntities();
    }
  }, [clientId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/clients/${clientId}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    try {
      const response = await axios.get(`/entities?client_id=${clientId}`);
      setEntities(response.data);
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/clients/${clientId}/users`, {
        ...newUser,
        client_id: clientId,
        client_role: 'client_admin' // HR can only create Client Admin users
      });
      toast.success('User added successfully');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', phone: '', password: '', assigned_entities: [] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!showEditUser) return;
    
    try {
      await axios.put(`/clients/${clientId}/users/${showEditUser.id}`, {
        name: showEditUser.name,
        email: showEditUser.email,
        phone: showEditUser.phone,
        client_role: 'client_admin',
        assigned_entities: showEditUser.assigned_entities || []
      });
      toast.success('User updated successfully');
      setShowEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeactivateUser = async (user) => {
    if (!window.confirm(`Are you sure you want to deactivate ${user.name}? They will no longer be able to login.`)) {
      return;
    }
    
    try {
      await axios.put(`/users/${user.id}`, {
        is_active: false
      });
      toast.success(`${user.name} has been deactivated`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.entity_name || entityId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Company Users</h1>
          <p className="text-slate-600">Manage users in your organization</p>
        </div>
        <Button onClick={() => setShowAddUser(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Assigned Entities</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.phone ? (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-3 h-3" />
                          {user.phone}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={user.client_role === 'client_admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                        <Shield className="w-3 h-3 mr-1" />
                        {user.client_role === 'client_admin' ? 'Admin' : 'User'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.client_role === 'client_admin' ? (
                        <span className="text-slate-500 text-sm">All Entities</span>
                      ) : user.assigned_entities?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.assigned_entities.map(entityId => (
                            <Badge key={entityId} variant="outline" className="text-xs">
                              {getEntityName(entityId)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={user.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {user.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowEditUser(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {user.is_active !== false && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivateUser(user)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`deactivate-user-${user.id}`}
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No users added yet</p>
              <Button onClick={() => setShowAddUser(true)} className="bg-slate-900 hover:bg-slate-800">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Your First User
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entities Overview */}
      {entities.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Company Entities ({entities.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {entities.map((entity) => {
                const entityUsers = users.filter(u => 
                  u.client_role === 'client_admin' || 
                  u.assigned_entities?.includes(entity.id)
                );
                return (
                  <div key={entity.id} className="border rounded-lg p-4 bg-slate-50">
                    <h4 className="font-medium text-slate-900">{entity.entity_name}</h4>
                    <p className="text-sm text-slate-500">{entity.location || 'No location'}</p>
                    <div className="mt-2 flex items-center gap-1 text-sm text-slate-600">
                      <Users className="w-3 h-3" />
                      {entityUsers.length} user(s) with access
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={newUser.name} 
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} 
                required 
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email" 
                value={newUser.email} 
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} 
                required 
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={newUser.phone} 
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} 
                data-testid="new-user-phone"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input 
                type="password" 
                value={newUser.password} 
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} 
                placeholder="Leave blank for default"
                data-testid="new-user-password"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <Shield className="w-4 h-4 inline mr-1" />
                New users will be created as <strong>Client Admin</strong> with full access to all entities.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-add-user">Add User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!showEditUser} onOpenChange={(open) => !open && setShowEditUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {showEditUser && (
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={showEditUser.name} 
                  onChange={(e) => setShowEditUser({ ...showEditUser, name: e.target.value })} 
                  required 
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email" 
                  value={showEditUser.email} 
                  disabled
                  className="bg-slate-100"
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={showEditUser.phone || ''} 
                  onChange={(e) => setShowEditUser({ ...showEditUser, phone: e.target.value })} 
                  data-testid="edit-user-phone"
                />
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Role: <strong>{showEditUser.client_role === 'client_admin' ? 'Client Admin' : 'Client User'}</strong>
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditUser(null)}>Cancel</Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-edit-user">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
